/* ─────────────────────────────────────────────────────────────
   KUNDA LONG-TERM — V1 (Increment 1)
   Discovery, listing creation, detail page, secure private location.

   The Requests system (viewing / rental requests, host inbox,
   notifications, contact sharing) belongs to Increment 2 and is
   deliberately NOT implemented here.
───────────────────────────────────────────────────────────────── */
import React, { useState, useMemo, useRef, useEffect } from "react";
import { IconArrowLeft, IconBed, IconBath, IconSofa, IconBuilding, PhotoCarousel } from "./ui";
import {
  collection,
  addDoc,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebase";
import { compressImage, extForBlob } from "./imageUtils";

/* ── Constants ─────────────────────────────────────────────── */
export var LT_TYPES = [
  { id: "apartment", label: "Apartment", icon: "🏢" },
  { id: "house",     label: "House",     icon: "🏠" },
  { id: "studio",    label: "Studio",    icon: "🛋" },
  { id: "room",      label: "Room",      icon: "🛏" },
  { id: "villa",     label: "Villa",     icon: "🏡" },
];

var LT_AMENITIES = [
  { id: "wifi",      label: "Wi-Fi",            icon: "📶" },
  { id: "ac",        label: "Air conditioning", icon: "❄️" },
  { id: "hot_water", label: "Hot water",        icon: "🚿" },
  { id: "generator", label: "Generator",        icon: "🔌" },
  { id: "parking",   label: "Parking",          icon: "🅿️" },
  { id: "security",  label: "Security",         icon: "🛡️" },
  { id: "water",     label: "Water",            icon: "💧" },
  { id: "kitchen",   label: "Kitchen",          icon: "🍳" },
  { id: "balcony",   label: "Balcony",          icon: "🌤" },
];

var LT_SHARED = [
  { id: "compound",  label: "Shared compound" },
  { id: "kitchen",   label: "Shared kitchen" },
  { id: "living",    label: "Shared living room" },
  { id: "pool",      label: "Shared pool" },
  { id: "rooftop",   label: "Shared rooftop" },
  { id: "laundry",   label: "Shared laundry" },
  { id: "parking",   label: "Shared parking" },
  { id: "garden",    label: "Shared garden" },
];

var FALLBACK =
  "data:image/svg+xml," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 800">' +
    '<rect width="800" height="800" fill="#F2EDE5"/>' +
    '<g fill="none" stroke="#A89B8B" stroke-width="16" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M400 270 L570 415 L545 415 L545 555 L255 555 L255 415 L230 415 Z"/>' +
    '<path d="M355 555 L355 465 L445 465 L445 555"/>' +
    "</g></svg>"
  );

function ltTypeLabel(id) {
  var t = LT_TYPES.find(function(x) { return x.id === id; });
  return t ? t.label : "Property";
}
function isLtApproved(l) {
  var s = (l.status || "").toLowerCase().trim();
  return (s === "approved" || s === "approve" || s === "active" || s === "live" || s === "") && !l.hidden;
}
function fmtGMD(n) { return "GMD " + (Number(n) || 0).toLocaleString(); }
/* Native share sheet where available, clipboard fallback otherwise —
   no new backend, just the platform's own share affordance. */
function shareListing(l, onToast) {
  var title = (l.title && l.title.trim()) || "Long-term rental on KUNDA";
  var url = window.location.href;
  if (navigator.share) {
    navigator.share({ title: title, text: title + " — " + (l.location || "The Gambia"), url: url }).catch(function() {});
  } else if (navigator.clipboard) {
    navigator.clipboard.writeText(url).then(function() {
      if (onToast) onToast("Link copied");
    }).catch(function() {});
  }
}

/* "2026-08-01" → "1 Aug 2026". Pure function of its argument, so it lives
   at module scope where every component in this file can reach it. */
/* Cards rendered per batch in the Long-Term feed. Fixed value, so it sits
   at module scope — inside the component it was recreated every render and
   ESLint flagged it as a missing useEffect dependency. */
var PAGE = 8;

/* Wizard step labels — a fixed literal, so module scope keeps it from
   being rebuilt on every render. */
var STEPS = ["Basics", "Details", "Photos", "Review"];

function prettyMonth(s) {
  if (!s) return "";
  var d = new Date(s + "T00:00:00");
  if (isNaN(d.getTime())) return s;
  var m = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return d.getDate() + " " + m[d.getMonth()] + " " + d.getFullYear();
}

function heartIcon(filled) {
  return (
    <svg viewBox="0 0 24 24" fill={filled ? "#C2185B" : "rgba(0,0,0,0.35)"}
         stroke="#fff" strokeWidth="1.8" style={{ width: 22, height: 22 }}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  );
}

/* ── LONG-TERM CARD ────────────────────────────────────────── */
function LtCardBase(props) {
  var l = props.listing;
  /* Prefer tiny thumbnails on the feed, exactly like Short Stay cards.
     The whole set is passed so the guest can swipe photos without
     opening the listing. */
  var photos = (l.photoThumbs && l.photoThumbs.length > 0)
    ? l.photoThumbs
    : ((l.photos && l.photos.length > 0) ? l.photos : (l.imageUrl ? [l.imageUrl] : []));
  var beds = Number(l.bedrooms) || 0;
  var baths = Number(l.bathrooms) || 0;

  var rentN = Number(l.monthlyRent) || 0;
  var agentPct = Math.max(0, Math.min(10, Number(l.agentFeePercent) || 0));
  var moveIn = rentN > 0 ? rentN + rentN + Math.round(rentN * agentPct / 100) : 0;

  return (
    <div className="lt-card" onClick={function() { props.onTap(l); }}>
      <div className="lt-card-imgwrap">
        <PhotoCarousel photos={photos}/>
        <button className={"lt-heart" + (props.saved ? " is-saved" : "")}
                aria-label={props.saved ? "Remove from saved" : "Save"}
                onClick={function(e) { e.stopPropagation(); props.onSave(l.id); }}>
          {heartIcon(props.saved)}
        </button>
      </div>
      <div className="lt-card-body">
        <div className="lt-card-title">{(l.title && l.title.trim()) || ltTypeLabel(l.propertyType)}</div>
        <div className="lt-card-loc">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
          <span>{l.location || "The Gambia"}</span>
        </div>
        <div className="lt-card-price"><strong>{fmtGMD(l.monthlyRent)}</strong><span> / month</span></div>

        {/* Icons governed by the shared .detv2-quick-item rule — 14px, --ink-2 */}
        <div className="lt-card-facts">
          {beds > 0 && (
            <span className="detv2-quick-item"><IconBed/><span>{beds} {beds === 1 ? "Bed" : "Beds"}</span></span>
          )}
          {baths > 0 && (
            <span className="detv2-quick-item"><IconBath/><span>{baths} {baths === 1 ? "Bath" : "Baths"}</span></span>
          )}
          {typeof l.furnished === "boolean" && (
            <span className="detv2-quick-item"><IconSofa/><span>{l.furnished ? "Furnished" : "Unfurnished"}</span></span>
          )}
        </div>

        {moveIn > 0 && (
          <div className="lt-card-movein">
            <svg className="lt-mi-doc" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <span className="lt-mi-t">Move-in cost: {fmtGMD(moveIn)}</span>
            <button className="lt-mi-i" aria-label="What is included in the move-in cost?"
                    onClick={function(e) {
                      e.stopPropagation();
                      props.onInfo && props.onInfo(rentN, agentPct);
                    }}>i</button>
          </div>
        )}

        {l.availableFrom && (
          <div className="lt-card-avail">Available from {prettyMonth(l.availableFrom)}</div>
        )}
      </div>
    </div>
  );
}
export var LtCard = React.memo(LtCardBase, function(a, b) {
  return a.listing === b.listing && a.saved === b.saved;
});

/* Loading placeholder — reuses KUNDA's existing .skel styles */
/* Compact budget label for the filter chip */
function priceLabel(v) {
  if (v === "u10") return "Under 10k";
  if (v === "u20") return "Under 20k";
  if (v === "u35") return "Under 35k";
  if (v === "o35") return "35k+";
  return "Monthly budget";
}

function LtSkeleton() {
  return (
    <div className="skel">
      <div className="skel-img"/>
      <div className="skel-line"/>
      <div className="skel-line short"/>
      <div className="skel-line" style={{ width: "45%" }}/>
    </div>
  );
}

/* ── LONG-TERM FEED (rendered inside ExplorePage) ──────────── */
export function LongTermFeed(props) {
  /* props: listings, q, saved, onSave, onTap, onBecomeHost */
  var [typeF, setTypeF] = useState("all");
  var [shown, setShown] = useState(PAGE);
  var [miInfo, setMiInfo] = useState(null);   /* move-in cost explainer */
  var [sheetOpen, setSheetOpen] = useState(false);
  var [priceF, setPriceF] = useState("any");   /* any|u10|u20|u35|o35 (thousands GMD) */
  var [bedF, setBedF] = useState("any");       /* any|1|2|3 */
  var [furnF, setFurnF] = useState("any");     /* any|yes|no */
  var [availNow, setAvailNow] = useState(false);

  var q = props.q || "";

  var rentals = useMemo(function() {
    return (props.listings || []).filter(function(l) {
      return l.rentalType === "long_term" && isLtApproved(l);
    });
  }, [props.listings]);

  var filtered = useMemo(function() {
    var qq = q.toLowerCase().trim();
    var today = new Date().toISOString().slice(0, 10);
    return rentals.filter(function(l) {
      if (qq) {
        var text = ((l.title || "") + " " + (l.location || "") + " " + (l.code || "") +
                    " " + ltTypeLabel(l.propertyType)).toLowerCase();
        if (!text.includes(qq)) return false;
      }
      if (typeF !== "all" && l.propertyType !== typeF) return false;
      var p = Number(l.monthlyRent) || 0;
      if (priceF === "u10" && !(p < 10000)) return false;
      if (priceF === "u20" && !(p < 20000)) return false;
      if (priceF === "u35" && !(p < 35000)) return false;
      if (priceF === "o35" && !(p >= 35000)) return false;
      if (bedF !== "any" && !((Number(l.bedrooms) || 0) >= Number(bedF))) return false;
      if (furnF === "yes" && l.furnished !== true) return false;
      if (furnF === "no" && l.furnished !== false) return false;
      if (availNow && l.availableFrom && l.availableFrom > today) return false;
      return true;
    });
  }, [rentals, q, typeF, priceF, bedF, furnF, availNow]);

  var advCount = (priceF !== "any" ? 1 : 0) + (bedF !== "any" ? 1 : 0) +
                 (furnF !== "any" ? 1 : 0) + (availNow ? 1 : 0);

  /* A new result set starts from the first page again */
  useEffect(function() { setShown(PAGE); }, [q, typeF, filtered.length]);


  function clearAll() {
    setTypeF("all"); setPriceF("any"); setBedF("any"); setFurnF("any"); setAvailNow(false);
  }

  var isFiltering = (q && q.trim()) || typeF !== "all" || advCount > 0;

  function grid(list) {
    var visible = list.slice(0, shown);
    return (
      <>
      <div className="grid lt-grid">
        {visible.map(function(l) {
          return (
            <LtCard key={l.id} listing={l} onTap={props.onTap}
                    saved={props.saved.includes(l.id)} onSave={props.onSave}
                    onInfo={function(rent, pct) { setMiInfo({ rent: rent, pct: pct }); }}/>
          );
        })}
      </div>
      {list.length > shown && (
        <button className="lt-more-btn" onClick={function() { setShown(shown + PAGE); }}>
          Show more homes ({list.length - shown} more)
        </button>
      )}
      </>
    );
  }

  return (
    <div className="lt-feed">
      {/* Type chips + Filters button */}
      {/* Long-Term filter row — three dropdowns plus the full filter sheet.
          NO location chip: location is chosen through the inline .ltloc
          panel that opens from the search bar. One place, one way. */}
      <div className="ltf-row">
        <button className={"ltf-chip" + (priceF !== "any" ? " on" : "")}
                onClick={function() { setSheetOpen(true); }}>
          {priceF !== "any" ? priceLabel(priceF) : "Monthly budget"}<span className="ltf-chev"/>
        </button>
        <button className={"ltf-chip" + (bedF !== "any" ? " on" : "")}
                onClick={function() { setSheetOpen(true); }}>
          {bedF !== "any" ? (bedF === "0" ? "Studio" : bedF + "+ beds") : "Bedrooms"}<span className="ltf-chev"/>
        </button>
        <button className={"ltf-chip" + (furnF !== "any" ? " on" : "")}
                onClick={function() { setSheetOpen(true); }}>
          {furnF === "yes" ? "Furnished" : furnF === "no" ? "Unfurnished" : "Furnished"}<span className="ltf-chev"/>
        </button>
        <button className={"ltf-sliders" + (advCount > 0 ? " on" : "")}
                aria-label="All filters"
                onClick={function() { setSheetOpen(true); }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/></svg>
        </button>
      </div>

      <div className="main">
        {props.loading ? (
          <div className="grid">
            {[1, 2, 3, 4, 5, 6].map(function(i) { return <LtSkeleton key={i}/>; })}
          </div>
        ) : rentals.length === 0 ? (
          <div className="empty">
            <div className="empty-emoji">🔑</div>
            <h3>Long-term rentals are arriving</h3>
            <p>Be among the first to list a monthly rental on KUNDA.</p>
            <button className="btn-primary" onClick={props.onBecomeHost}>List a Long-Term property</button>
          </div>
        ) : isFiltering ? (
          filtered.length > 0 ? (
            <>
              <div className="results-count">{filtered.length} {filtered.length === 1 ? "rental" : "rentals"} found</div>
              {grid(filtered)}
            </>
          ) : (
            <div className="empty">
              <div className="empty-emoji">🔍</div>
              <h3>No rentals found yet</h3>
              <p>Try another area or adjust your filters.</p>
              <button className="btn-primary" onClick={clearAll}>Clear filters</button>
            </div>
          )
        ) : (
          <>
            <div className="ltf-head">
              <h2 className="lt-h">Find a place to call home</h2>
              <p className="ltf-sub">Quality long-term homes across The Gambia</p>
            </div>

            {/* Straight from the subtitle into the cards — nothing between. */}
            {grid(filtered)}
          </>
        )}
      </div>

      {/* Filter sheet */}
      {/* Move-in cost explainer — states exactly what the number contains */}
      {miInfo && (
        <div className="bsheet-overlay" onClick={function() { setMiInfo(null); }}>
          <div className="bsheet" onClick={function(e) { e.stopPropagation(); }}>
            <div className="bsheet-handle"/>
            <h2 className="bsheet-title">What is the move-in cost?</h2>
            <div className="ltd-costs-card">
              <div className="ltd-costs-row"><span>First month rent</span><span>{fmtGMD(miInfo.rent)}</span></div>
              <div className="ltd-costs-row"><span>Security deposit — 1 month</span><span>{fmtGMD(miInfo.rent)}</span></div>
              {miInfo.pct > 0 ? (
                <div className="ltd-costs-row"><span>Agent fee — {miInfo.pct}%</span><span>{fmtGMD(Math.round(miInfo.rent * miInfo.pct / 100))}</span></div>
              ) : null}
            </div>
            <div className="ltw-hint">
              An estimate to help you plan. KUNDA does not charge a fee — any agent fee is set by the
              listing agent. Confirm all amounts with the host before making any payment.
            </div>
            <button className="bsheet-continue" onClick={function() { setMiInfo(null); }}>Got it</button>
          </div>
        </div>
      )}

      {sheetOpen && (
        <div className="bsheet-overlay" onClick={function() { setSheetOpen(false); }}>
          <div className="bsheet fsheet" onClick={function(e) { e.stopPropagation(); }}>
            <div className="bsheet-handle"/>
            <h2 className="bsheet-title">Filters</h2>

            <div className="fsheet-group">
              <div className="fsheet-label">Monthly rent</div>
              <div className="fsheet-opts">
                {[["any","Any"],["u10","Under 10,000"],["u20","Under 20,000"],["u35","Under 35,000"],["o35","35,000+"]].map(function(o) {
                  return <button key={o[0]} className={"fsheet-opt" + (priceF === o[0] ? " on" : "")}
                                 onClick={function() { setPriceF(o[0]); }}>{o[1]}</button>;
                })}
              </div>
            </div>

            <div className="fsheet-group">
              <div className="fsheet-label">Bedrooms</div>
              <div className="fsheet-opts">
                {[["any","Any"],["1","1+"],["2","2+"],["3","3+"]].map(function(o) {
                  return <button key={o[0]} className={"fsheet-opt" + (bedF === o[0] ? " on" : "")}
                                 onClick={function() { setBedF(o[0]); }}>{o[1]}</button>;
                })}
              </div>
            </div>

            <div className="fsheet-group">
              <div className="fsheet-label">Furnishing</div>
              <div className="fsheet-opts">
                {[["any","Any"],["yes","Furnished"],["no","Unfurnished"]].map(function(o) {
                  return <button key={o[0]} className={"fsheet-opt" + (furnF === o[0] ? " on" : "")}
                                 onClick={function() { setFurnF(o[0]); }}>{o[1]}</button>;
                })}
              </div>
            </div>

            <div className="fsheet-group">
              <div className="fsheet-opts">
                <button className={"fsheet-opt" + (availNow ? " on" : "")}
                        onClick={function() { setAvailNow(!availNow); }}>Available now</button>
              </div>
            </div>

            <div className="fsheet-actions">
              <button className="fsheet-clear" onClick={clearAll}>Clear all</button>
              <button className="fsheet-apply" onClick={function() { setSheetOpen(false); }}>
                Show {filtered.length} {filtered.length === 1 ? "rental" : "rentals"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── FULL-SCREEN GALLERY — tap the hero to open, swipe like Apple
   Photos, Cancel/× to close. Identical component/CSS to the one Short-Stay
   already ships (App.js FullScreenGallery + .fsg-* classes) — reimplemented
   here locally only because App.js and longterm.js can't import from each
   other without a circular dependency. Nothing about the experience differs. */
function LtFullScreenGallery(props) {
  var photos = props.photos && props.photos.length > 0 ? props.photos : [FALLBACK];
  var [idx, setIdx] = useState(props.startIndex || 0);
  var [touchStart, setTouchStart] = useState(null);
  var [dragX, setDragX] = useState(0);

  function onTouchStart(e) { setTouchStart(e.touches[0].clientX); setDragX(0); }
  function onTouchMove(e) {
    if (touchStart === null) return;
    var diff = e.touches[0].clientX - touchStart;
    if ((idx === 0 && diff > 0) || (idx === photos.length - 1 && diff < 0)) diff = diff * 0.35;
    setDragX(diff);
  }
  function onTouchEnd(e) {
    if (touchStart === null) return;
    var diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && idx < photos.length - 1) setIdx(idx + 1);
      else if (diff < 0 && idx > 0) setIdx(idx - 1);
    }
    setTouchStart(null);
    setDragX(0);
  }

  var trackStyle = {
    transform: "translateX(calc(" + (-idx * 100) + "% + " + dragX + "px))",
    transition: touchStart === null ? "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)" : "none"
  };

  return (
    <div className="fsg">
      <div className="fsg-top">
        <div className="fsg-counter">{(idx + 1) + " / " + photos.length}</div>
        <button className="fsg-close" onClick={props.onClose} aria-label="Close gallery">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div className="fsg-stage" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <div className="fsg-track" style={trackStyle}>
          {photos.map(function(p, i) {
            return (
              <div className="fsg-slide" key={i}>
                <img src={p} alt="" loading="lazy" decoding="async" onError={function(e) { e.target.src = FALLBACK; }}/>
              </div>
            );
          })}
        </div>
      </div>

      {photos.length > 1 && (
        <div className="fsg-dots">
          {photos.map(function(_, i) {
            return <button key={i} className={"fsg-dot " + (i === idx ? "on" : "")}
                           onClick={function() { setIdx(i); }} aria-label={"Photo " + (i + 1)}/>;
          })}
        </div>
      )}
    </div>
  );
}

/* ── LONG-TERM DETAIL PAGE ─────────────────────────────────── */
export function LongTermDetail(props) {
  /* props: listing, saved, onSave, onBack, onToast */
  var l = props.listing;
  var photos = (l.photos && l.photos.length > 0) ? l.photos : (l.imageUrl ? [l.imageUrl] : [FALLBACK]);
  var [idx, setIdx] = useState(0);
  var [expanded, setExpanded] = useState(false);
  var [touchStart, setTouchStart] = useState(null);
  var [dragX, setDragX] = useState(0);
  var movedRef = useRef(false);
  var [galleryOpen, setGalleryOpen] = useState(false);

  /* Swipeable gallery — same drag/rubber-band/threshold interaction as the
     Short-Stay carousel (App.js PhotoCarousel), reimplemented locally to
     avoid a circular import between App.js and longterm.js. */
  function galTouchStart(e) {
    movedRef.current = false;
    setTouchStart(e.touches[0].clientX);
    setDragX(0);
  }
  function galTouchMove(e) {
    if (touchStart === null) return;
    var diff = e.touches[0].clientX - touchStart;
    if (Math.abs(diff) > 8) movedRef.current = true;
    if ((idx === 0 && diff > 0) || (idx === photos.length - 1 && diff < 0)) diff = diff * 0.35;
    setDragX(diff);
  }
  function galTouchEnd(e) {
    if (touchStart === null) return;
    var diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && idx < photos.length - 1) setIdx(idx + 1);
      else if (diff < 0 && idx > 0) setIdx(idx - 1);
    }
    setTouchStart(null);
    setDragX(0);
  }
  /* Tap (not swipe) opens the full-screen gallery, exactly like Short-Stay */
  function galClick() {
    if (!movedRef.current) setGalleryOpen(true);
  }
  var galTrackStyle = {
    transform: "translateX(calc(" + (-idx * 100) + "% + " + dragX + "px))",
    transition: touchStart === null ? "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)" : "none"
  };

  var amen = l.amenities || [];
  var shared = l.sharedSpaces || [];
  var beds = Number(l.bedrooms) || 0;
  var baths = Number(l.bathrooms) || 0;
  var rentNum = Number(l.monthlyRent) || 0;
  /* Agent fee is whatever the agent chose (0 = none). KUNDA charges nothing. */
  var agentPct = Math.max(0, Math.min(10, Number(l.agentFeePercent) || 0));
  var agentAmt = Math.round(rentNum * agentPct / 100);
  var moveInTotal = rentNum + rentNum + agentAmt;
  var title = (l.title && l.title.trim()) || ltTypeLabel(l.propertyType);
  var desc = l.description || "A quality long-term rental in The Gambia.";
  var isLong = desc.length > 220;




  return (
    <>
    <div className="ltd">
      {/* Gallery — swipeable, same interaction as Short-Stay */}
      <div className="ltd-hero"
           onTouchStart={galTouchStart} onTouchMove={galTouchMove} onTouchEnd={galTouchEnd}
           onClick={galClick} style={{ cursor: "zoom-in" }}>
        <div className="car-track" style={galTrackStyle}>
          {photos.map(function(p, i) {
            return (
              <div className="car-slide" key={i}>
                <img src={p} alt="" loading={i === 0 ? "eager" : "lazy"} decoding="async"
                     onError={function(e) { e.target.src = FALLBACK; }}
                     onLoad={function(e) { e.target.classList.add("img-loaded"); }}/>
              </div>
            );
          })}
        </div>
        <div className="ltd-hero-tag">LONG-TERM</div>
        {photos.length > 1 && (
          <div className="car-counter">{(idx + 1) + " / " + photos.length}</div>
        )}
        {photos.length > 1 && (
          <div className="car-dots">
            {photos.map(function(_, i) { return <span key={i} className={"car-dot " + (i === idx ? "on" : "")}/>; })}
          </div>
        )}
        {photos.length > 1 && (
          <div className="car-viewall">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            View photos
          </div>
        )}
        <button className="ltd-back" onClick={function(e) { e.stopPropagation(); props.onBack(); }} aria-label="Back">
          <IconArrowLeft/>
        </button>
        <div className="ltd-hero-actions">
          <button className="ltd-share" onClick={function(e) { e.stopPropagation(); shareListing(l, props.onToast); }} aria-label="Share">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
          </button>
          <button className={"ltd-heart" + (props.saved ? " is-saved" : "")} onClick={function(e) { e.stopPropagation(); props.onSave(l.id); }}
                  aria-label={props.saved ? "Remove from saved" : "Save"}>
            {heartIcon(props.saved)}
          </button>
        </div>
      </div>

        {photos.length > 1 && (
          <div className="ltd-thumbs">
            {photos.slice(0, 8).map(function(p, i) {
              return (
                <button key={i} className={"ltd-thumb" + (i === idx ? " on" : "")}
                        onClick={function(e) { e.stopPropagation(); setIdx(i); }}>
                  <img src={p} alt="" loading="lazy" decoding="async"
                       onError={function(e) { e.target.src = FALLBACK; }}/>
                </button>
              );
            })}
          </div>
        )}


      <div className="ltd-body">
        <div className="ltd-titlerow">
          <h1 className="ltd-title">{title}</h1>
          {l.code && <span className="ltd-code">{l.code}</span>}
        </div>
        <div className="ltd-loc">📍 {(l.location || "The Gambia") + (/gambia/i.test(l.location || "") ? "" : ", The Gambia")}</div>
        <div className="ltd-price"><strong>{fmtGMD(l.monthlyRent)}</strong><span> / month</span></div>

        {/* Quick facts — uses Short Stay's literal .detv2-quick-item class,
            so icon size, colour and spacing are governed by one rule. */}
        <div className="ltd-quick">
          {beds > 0 && (
            <div className="detv2-quick-item">
              <IconBed/>
              <span>{beds} {beds === 1 ? "Bedroom" : "Bedrooms"}</span>
            </div>
          )}
          {baths > 0 && (
            <>
              <div className="detv2-quick-sep"/>
              <div className="detv2-quick-item">
                <IconBath/>
                <span>{baths} {baths === 1 ? "Bathroom" : "Bathrooms"}</span>
              </div>
            </>
          )}
          {typeof l.furnished === "boolean" && (
            <>
              <div className="detv2-quick-sep"/>
              <div className="detv2-quick-item">
                <IconSofa/>
                <span>{l.furnished ? "Furnished" : "Unfurnished"}</span>
              </div>
            </>
          )}
          <div className="detv2-quick-sep"/>
          <div className="detv2-quick-item">
            <IconBuilding/>
            <span>{ltTypeLabel(l.propertyType)}</span>
          </div>
        </div>

        {/* Move-in costs — a clear, honest estimate the guest can plan around */}
        {rentNum > 0 && (
          <div className="ltd-costs">
            <div className="ltd-costs-card">
              <div className="ltd-costs-top">
                <span className="ltd-costs-h">Move-in costs <em>(Estimated)</em></span>
                <strong>{fmtGMD(moveInTotal)}</strong>
              </div>
              <div className="ltd-costs-row"><span>First month rent</span><span>{fmtGMD(rentNum)}</span></div>
              <div className="ltd-costs-row"><span>Security deposit — 1 month</span><span>{fmtGMD(rentNum)}</span></div>
              {agentPct > 0 ? (
                <div className="ltd-costs-row"><span>Agent fee — {agentPct}%</span><span>{fmtGMD(agentAmt)}</span></div>
              ) : null}
            </div>
          </div>
        )}

        {/* Move-in information */}
        {(l.availableFrom || l.minimumStayMonths) && (
          <div className="ltd-chips">
            {l.minimumStayMonths ? (
              <div className="ltd-chip">
                <span className="ltd-chip-ic">🗓</span>
                <span className="ltd-chip-tx">
                  <span className="ltd-chip-l">Minimum stay</span>
                  <span className="ltd-chip-v">{l.minimumStayMonths} {Number(l.minimumStayMonths) === 1 ? "month" : "months"}</span>
                </span>
              </div>
            ) : null}
            {l.availableFrom ? (
              <div className="ltd-chip">
                <span className="ltd-chip-ic">🗓</span>
                <span className="ltd-chip-tx">
                  <span className="ltd-chip-l">Available from</span>
                  <span className="ltd-chip-v">{prettyMonth(l.availableFrom)}</span>
                </span>
              </div>
            ) : null}
          </div>
        )}

        {l.houseRules && l.houseRules.trim() ? (
          <>
            <h2 className="ltd-h">House rules</h2>
            <p className="ltd-p open">{l.houseRules}</p>
          </>
        ) : null}

        <h2 className="ltd-h">About this property</h2>
        <p className={"ltd-p" + (expanded || !isLong ? " open" : "")}>{desc}</p>
        {isLong && (
          <button className="ltd-more" onClick={function() { setExpanded(!expanded); }}>
            {expanded ? "Read less" : "Read more"}
          </button>
        )}

        {amen.length > 0 && (
          <>
            <h2 className="ltd-h">Amenities</h2>
            <div className="detv2-amen-grid">
              {amen.map(function(id) {
                var a = LT_AMENITIES.find(function(x) { return x.id === id; });
                return a ? (
                  <div key={id} className="detv2-amen">
                    <span className="detv2-amen-icon">{a.icon}</span>
                    <span className="detv2-amen-label">{a.label}</span>
                  </div>
                ) : null;
              })}
            </div>
          </>
        )}

        {/* Shared spaces — only when the listing actually has them */}
        {shared.length > 0 && (
          <>
            <h2 className="ltd-h">Public area</h2>
            <p className="ltd-p open">
              {shared.map(function(id) {
                var s = LT_SHARED.find(function(x) { return x.id === id; });
                return s ? s.label : null;
              }).filter(Boolean).join(", ")}
              {shared.length === 1 ? " is a shared space." : " are shared spaces."}
            </p>
          </>
        )}

        {/* Listed by — identity only; contact stays controlled (Requests, Inc 2) */}
        <h2 className="ltd-h">Listed by</h2>
        {agentPct > 0 && <div className="ltd-agent-tag">Agent listing</div>}
        <div className="ltd-host">
          <div className="ltd-host-av">
            {((l.hostBusinessName && l.hostBusinessName.trim()) || "K").charAt(0).toUpperCase()}
          </div>
          <div className="ltd-host-info">
            <div className="ltd-host-name">
              {(l.hostBusinessName && l.hostBusinessName.trim()) || "KUNDA verified host"}
              <span className="ltd-host-badge" title="Verified by KUNDA">✓</span>
            </div>
            <div className="ltd-host-sub">Verified host on KUNDA</div>
          </div>
        </div>

        <div className="ltd-trust">Exact location and host contact are shared privately after your request is accepted — that's how KUNDA keeps both sides safe.</div>
      </div>

      {/* The two primary actions (Requests system) */}
      {/* Single action — KUNDA introduces people, it does not process rentals. */}
      <div className="ltd-cta ltd-cta-single">
        <button className="ltd-cta-contact" onClick={function() { props.onRequest(l, "contact"); }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          Request contact
        </button>
      </div>
    </div>

    {galleryOpen && (
      <LtFullScreenGallery photos={photos} startIndex={idx}
                           onClose={function() { setGalleryOpen(false); }}/>
    )}
    </>
  );
}

/* ── LISTING TYPE CHOOSER ──────────────────────────────────── */
export function ListingTypeChooser(props) {
  /* props: onPick("short"|"long"), onCancel */
  var [pick, setPick] = useState("");
  return (
    <div className="bsheet-overlay" onClick={props.onCancel}>
      <div className="bsheet" onClick={function(e) { e.stopPropagation(); }}>
        <div className="bsheet-handle"/>
        <h2 className="bsheet-title">What are you listing?</h2>
        <button type="button" className={"ltc-opt" + (pick === "short" ? " on" : "")}
                onClick={function() { setPick("short"); }}>
          <span className="ltc-emoji">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/>
              <path d="M9 21v-5a3 3 0 0 1 6 0v5"/>
            </svg>
          </span>
          <span className="ltc-text"><strong>Short stay</strong><span>Nightly bookings for travelers</span></span>
          <span className="ltc-check">{pick === "short" ? "✓" : ""}</span>
        </button>
        <button type="button" className={"ltc-opt" + (pick === "long" ? " on" : "")}
                onClick={function() { setPick("long"); }}>
          <span className="ltc-emoji">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="8" cy="14" r="4.2"/><path d="M11 11.2 20 3"/>
              <path d="M17.2 5.8 19.4 8"/><path d="M14.6 8.4l1.8 1.8"/>
            </svg>
          </span>
          <span className="ltc-text"><strong>Long-term rental</strong><span>Monthly rentals for residents</span></span>
          <span className="ltc-check">{pick === "long" ? "✓" : ""}</span>
        </button>
        <button className={"bsheet-continue" + (pick ? "" : " is-off")} disabled={!pick}
                onClick={function() { if (pick) props.onPick(pick); }}>
          Continue
        </button>
      </div>
    </div>
  );
}

/* ── LONG-TERM LISTING WIZARD — 4-step professional flow ────
   Step 1 Basics → Step 2 Details → Step 3 Photos → Step 4 Review.
   Same storage pipeline, same private-location model, same dual-write. */
/* Number picker — identical markup and classes to the Short-Stay wizard's
   Stepper (App.js). Kept local because App.js and longterm.js can't import
   from each other without a circular dependency. */
function LtStepper(props) {
  var min = props.min === undefined ? 0 : props.min;
  var max = props.max === undefined ? 20 : props.max;
  function dec() { if (props.value > min) props.onChange(props.value - 1); }
  function inc() { if (props.value < max) props.onChange(props.value + 1); }
  return (
    <div className="stepper">
      <button type="button" className="step-btn" onClick={dec} disabled={props.value <= min}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
      </button>
      <div className="step-val">
        <div className="step-num">{props.display || props.value}</div>
        <div className="step-lbl">{props.label}</div>
      </div>
      <button type="button" className="step-btn" onClick={inc} disabled={props.value >= max}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
      </button>
    </div>
  );
}

export function LongTermWizard(props) {
  /* props: user, onCancel, onDone */
  var [step, setStep] = useState(0);
  var [title, setTitle] = useState("");
  var [propType, setPropType] = useState("apartment");
  var [area, setArea] = useState("");
  var [privAddress, setPrivAddress] = useState("");
  var [privMaps, setPrivMaps] = useState("");
  var [rent, setRent] = useState("");
  /* Agent fee — OFF by default. KUNDA never charges one; the agent
     chooses whether to, and at what percentage (max 10%). */
  var [agentFee, setAgentFee] = useState(false);
  var [agentPct, setAgentPct] = useState(5);
  var [bedrooms, setBedrooms] = useState(1);
  var [bathrooms, setBathrooms] = useState(1);
  var [maxOccupants, setMaxOccupants] = useState(2);
  var [sizeSqm, setSizeSqm] = useState("");
  var [furnished, setFurnished] = useState(true);
  var [availableFrom, setAvailableFrom] = useState("");
  var [minMonths, setMinMonths] = useState(6);
  var [description, setDescription] = useState("");
  var [houseRules, setHouseRules] = useState("");
  var [amen, setAmen] = useState([]);
  var [shared, setShared] = useState([]);
  var [noShared, setNoShared] = useState(false);
  var [photoFiles, setPhotoFiles] = useState([]);
  var [photoPreviews, setPhotoPreviews] = useState([]);
  var [dragOver, setDragOver] = useState(false);
  var [busy, setBusy] = useState(false);
  var [phase, setPhase] = useState("");
  var [error, setError] = useState("");
  var [privWarn, setPrivWarn] = useState("");
  var [done, setDone] = useState(false);

  var todayStr = new Date().toISOString().slice(0, 10);
  var rentNum = Number(rent) || 0;


  /* The form scrolls inside .ltw-body (not the window), so step changes must
     reset THAT element's scroll position — window.scrollTo has no effect here. */
  var bodyRef = useRef(null);
  function scrollFormTop() {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }

  function toggle(list, setList, id) {
    setList(list.indexOf(id) === -1 ? list.concat([id]) : list.filter(function(x) { return x !== id; }));
  }

  /* ── Photos: tap OR drag-and-drop, 5–20, reorder, delete ── */
  function addFiles(files) {
    var imgs = Array.from(files || []).filter(function(f) { return f.type && f.type.indexOf("image/") === 0; });
    if (!imgs.length) return;
    var next = photoFiles.concat(imgs).slice(0, 20);
    setPhotoFiles(next);
    setPhotoPreviews(next.map(function(f) { return URL.createObjectURL(f); }));
  }
  function onPhotos(e) {
    /* e.target.files is a LIVE FileList tied to the input — clearing the input
       empties it. Snapshot into a real array FIRST, then reset the input so the
       same file can be picked again. */
    var files = Array.from(e.target.files || []);
    e.target.value = "";
    addFiles(files);
  }
  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer && e.dataTransfer.files);
  }
  function removePhoto(i) {
    var next = photoFiles.slice(); next.splice(i, 1);
    setPhotoFiles(next);
    setPhotoPreviews(next.map(function(f) { return URL.createObjectURL(f); }));
  }
  function movePhoto(i, dir) {
    var j = i + dir;
    if (j < 0 || j >= photoFiles.length) return;
    var next = photoFiles.slice();
    var t = next[i]; next[i] = next[j]; next[j] = t;
    setPhotoFiles(next);
    setPhotoPreviews(next.map(function(f) { return URL.createObjectURL(f); }));
  }

  /* ── Per-step validation (inline, blocks progression) ── */
  function stepMissing(s) {
    var m = [];
    if (s === 0) {
      if (title.trim().length < 4)        m.push("a property title (at least 4 characters)");
      if (area.trim().length < 2)         m.push("the public area");
      if (privAddress.trim().length < 6)  m.push("the exact private address");
      if (!(rentNum > 0))                 m.push("the monthly rent");
    }
    if (s === 1) {
      if (description.trim().length < 20) m.push("a description (at least 20 characters)");
    }
    if (s === 2) {
      if (photoFiles.length < 5)          m.push((5 - photoFiles.length) + " more photo" + (photoFiles.length === 4 ? "" : "s") + " (minimum 5)");
    }
    return m;
  }
  function goNext() {
    var missing = stepMissing(step);
    if (missing.length) {
      setError("Almost there — please add " + missing.join(", ") + ".");
      return;
    }
    setError("");
    setStep(step + 1);
    scrollFormTop();
  }
  function goBack() {
    setError("");
    if (step === 0) { props.onCancel(); return; }
    setStep(step - 1);
    scrollFormTop();
  }

  function genCode() {
    return "LT-" + Math.floor(1000 + Math.random() * 9000);
  }

  async function submit() {
    if (busy) return;
    var all = stepMissing(0).concat(stepMissing(1)).concat(stepMissing(2));
    if (all.length > 0) { setError("Almost there — please add " + all.join(", ") + "."); return; }
    if (!props.user || !props.user.uid) { setError("Please log in to list your property."); return; }
    setBusy(true);
    setError("");
    try {
      setPhase("Optimizing your photos…");
      var fulls = await Promise.all(photoFiles.map(function(f) { return compressImage(f, 1600, 0.82); }));
      var thumbs = await Promise.all(photoFiles.map(function(f) { return compressImage(f, 520, 0.74); }));

      var stamp = Date.now();
      function up(blob, name) {
        var r = sRef(storage, name);
        var meta = { cacheControl: "public, max-age=31536000, immutable" };
        return uploadBytes(r, blob, meta).then(function(snap) { return getDownloadURL(snap.ref); });
      }
      var photoUrls = [];
      var thumbUrls = [];
      for (var i = 0; i < fulls.length; i++) {
        setPhase("Uploading photo " + (i + 1) + " of " + fulls.length + "…");
        /* eslint-disable no-await-in-loop */
        var fu = await up(fulls[i], "listings/" + stamp + "_lt" + i + extForBlob(fulls[i]));
        var tu = await up(thumbs[i], "listings/" + stamp + "_lt" + i + "_thumb" + extForBlob(thumbs[i]));
        /* eslint-enable no-await-in-loop */
        photoUrls.push(fu);
        thumbUrls.push(tu);
      }

      setPhase("Publishing your listing…");
      var docRef = await addDoc(collection(db, "listings"), {
        rentalType:        "long_term",
        code:              genCode(),
        title:             title.trim(),
        propertyType:      propType,
        location:          area.trim(),
        description:       description.trim(),
        houseRules:        houseRules.trim(),
        monthlyRent:       rentNum,
        agentFeePercent:   agentFee ? Number(agentPct) : 0,
        pricePerNight:     rentNum,
        bedrooms:          Number(bedrooms) || 0,
        bathrooms:         Number(bathrooms) || 0,
        maxOccupants:      Number(maxOccupants) || 0,
        sizeSqm:           Number(sizeSqm) || 0,
        furnished:         !!furnished,
        availableFrom:     availableFrom || "",
        minimumStayMonths: Number(minMonths) || 0,
        amenities:         amen,
        sharedSpaces:      noShared ? [] : shared,
        guests:            0,
        photos:            photoUrls,
        photoThumbs:       thumbUrls,
        imageUrl:          photoUrls[0] || "",
        hostId:            props.user.uid,
        hostBusinessName:  (props.user.name || ""),
        status:            "pending",
        hidden:            false,
        createdAt:         serverTimestamp(),
        updatedAt:         serverTimestamp(),
      });

      /* 🔒 PRIVATE LOCATION — separate, rules-protected subcollection.
         Isolated try/catch on purpose: the listing document already exists by
         now, so throwing here would send the host back to re-submit and create
         a DUPLICATE listing. If only this write fails, the listing still stands
         and we tell the host exactly what's missing. */
      try {
        await setDoc(doc(db, "listings", docRef.id, "private", "location"), {
          address:   privAddress.trim(),
          mapsLink:  privMaps.trim(),
          hostId:    props.user.uid,
          createdAt: serverTimestamp(),
        });
      } catch (privErr) {
        console.error("KUNDA: private location write failed —", privErr && privErr.code, privErr);
        setPrivWarn(
          "Your listing was submitted, but the private location couldn't be saved" +
          (privErr && privErr.code ? " (" + privErr.code + ")" : "") +
          ". Please open the listing and add it again before sharing with a guest."
        );
      }

      setBusy(false);
      setDone(true);
    } catch (e) {
      /* Surface the real cause instead of a generic message */
      console.error("KUNDA: listing submit failed —", e && e.code, e);
      setBusy(false);
      setError(
        "Something went wrong" +
        (e && e.code ? " (" + e.code + ")" : "") +
        ". Check your connection and try again."
      );
    }
  }

  function resetForAnother() {
    setDone(false); setStep(0);
    setTitle(""); setArea(""); setPrivAddress(""); setPrivMaps(""); setRent("");
    setBedrooms(1); setBathrooms(1); setMaxOccupants(2); setSizeSqm("");
    setFurnished(true); setAvailableFrom(""); setMinMonths(6);
    setDescription(""); setHouseRules(""); setAmen([]); setShared([]); setNoShared(false);
    setPhotoFiles([]); setPhotoPreviews([]); setError(""); setPrivWarn("");
    window.scrollTo(0, 0);
  }

  if (done) {
    return (
      <div className="wiz">
        <div className="success-screen">
          <div className="success-check">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h1 className="success-title">Listing submitted</h1>
          <p className="success-sub">Your property has been submitted for review. Our team will review it before it becomes visible on KUNDA — track its status on your dashboard.</p>
          {privWarn ? <div className="ltw-error">{privWarn}</div> : null}
          <button className="btn-primary success-btn" onClick={function() { if (props.onDone) props.onDone(); }}>
            Return to dashboard
          </button>
          <button className="ltw-again" onClick={resetForAnother}>Add another property</button>
        </div>
      </div>
    );
  }

  return (
    <div className="wiz ltw">
      {busy && (
        <div className="wiz-loading">
          <div className="wiz-loading-box">
            <div className="wiz-loading-ring"/>
            <div className="wiz-loading-title">{phase || "Submitting…"}</div>
            <div className="wiz-loading-sub">Hang tight — this won't take long</div>
          </div>
        </div>
      )}

      {/* Header — the shared Short-Stay wizard pattern. .wiz-progress and
          .wiz-step MUST sit inside .wiz-top: it supplies the flex ROW they
          were written for. Outside it, flex:1 grows the bar vertically into
          a full-height green block. */}
      <div className="wiz-top">
        <button className="wiz-close" onClick={props.onCancel} aria-label="Cancel" disabled={busy}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <div className="wiz-progress">
          <div className="wiz-progress-fill" style={{ width: (((step + 1) / STEPS.length) * 100) + "%" }}/>
        </div>
        <div className="wiz-step">{step + 1} / {STEPS.length}</div>
      </div>

      <div className="ltw-body" ref={bodyRef}>
        <h1 className="wiz-h1">{STEPS[step]}</h1>
        {/* ═══ STEP 1 — BASICS ═══ */}
        {step === 0 && (
          <>
            <div className="ltw-group">
              <label className="ltw-label">Property title</label>
              <input className="wiz-input" value={title} maxLength={80}
                     placeholder="e.g. 2 Bedroom Apartment"
                     onChange={function(e) { setTitle(e.target.value); }}/>
            </div>
            <div className="ltw-group">
              <label className="ltw-label">Property type</label>
              <div className="wiz-grid wiz-grid-types">
                {LT_TYPES.map(function(t) {
                  return (
                    <button key={t.id} type="button"
                            className={"wiz-card " + (propType === t.id ? "is-selected" : "")}
                            onClick={function() { setPropType(t.id); }}>
                      <div className="wiz-card-icon">{t.icon}</div>
                      <div className="wiz-card-label">{t.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="ltw-group">
              <label className="ltw-label">Public area</label>
              <input className="wiz-input" value={area} maxLength={60}
                     placeholder="e.g. Bijilo"
                     onChange={function(e) { setArea(e.target.value); }}/>
              <div className="ltw-hint">This area will be shown publicly on your listing.</div>
            </div>
            <div className="ltw-group ltw-private">
              <label className="ltw-label">🔒 Private property location</label>
              <input className="wiz-input" value={privAddress} maxLength={140}
                     placeholder="Exact address / compound / landmark"
                     onChange={function(e) { setPrivAddress(e.target.value); }}/>
              <div className="ltw-hint">Your exact location is kept private and is only shared if you choose to share it with an interested guest.</div>
            </div>
            <div className="ltw-group">
              <label className="ltw-label">Google Maps location</label>
              <input className="wiz-input" value={privMaps} maxLength={200}
                     placeholder="Google Maps link (optional)"
                     onChange={function(e) { setPrivMaps(e.target.value); }}/>
              <div className="ltw-hint">Optional. You can share the exact map location with an interested guest later.</div>
            </div>
            <div className="ltw-group">
              <label className="ltw-label">Monthly rent</label>
              <div className="wiz-price-input-wrap">
                <span className="wiz-price-currency">GMD</span>
                <input className="wiz-input wiz-price-input" type="number" inputMode="numeric" min="0"
                       value={rent} placeholder="0"
                       onChange={function(e) { setRent(e.target.value); }}/>
                <span className="wiz-price-suffix">/ month</span>
              </div>
            </div>

            {/* Asked right after rent so nobody skips it */}
            <div className="ltw-group">
              <button type="button" className={"rq-toggle-row" + (agentFee ? " on" : "")}
                      onClick={function() { setAgentFee(!agentFee); }}>
                <span className="rq-toggle-text">
                  <span className="rq-toggle-t">Are you charging an agent fee?</span>
                  <span className="rq-toggle-s">
                    {agentFee ? "The guest will see this fee clearly on the listing." : "No agent fee"}
                  </span>
                </span>
                <span className="rq-toggle-sw"><span className="rq-toggle-knob"/></span>
              </button>
            </div>

            {agentFee && (
              <div className="ltw-group">
                <label className="ltw-label">Agent fee</label>
                <LtStepper value={agentPct} min={1} max={10} label="Percentage of one month's rent"
                           display={agentPct + "%"}
                           onChange={setAgentPct}/>
                <div className="ltw-agentcalc">
                  <span>Guest pays</span>
                  <strong>{fmtGMD(Math.round((Number(rent) || 0) * agentPct / 100))}</strong>
                </div>
                <div className="ltw-hint">Calculated from one month&rsquo;s rent. Maximum 10%.</div>
              </div>
            )}
          </>
        )}

        {/* ═══ STEP 2 — DETAILS ═══ */}
        {step === 1 && (
          <>
            <div className="ltw-group">
              <LtStepper value={bedrooms} min={0} max={10} label="Bedrooms"
                         display={bedrooms === 0 ? "Studio" : bedrooms}
                         onChange={setBedrooms}/>
            </div>
            <div className="ltw-group">
              <LtStepper value={bathrooms} min={1} max={10} label="Bathrooms"
                         onChange={setBathrooms}/>
            </div>
            <div className="ltw-group">
              <LtStepper value={maxOccupants} min={1} max={16} label="Maximum occupants"
                         onChange={setMaxOccupants}/>
            </div>
            <div className="ltw-group">
              <label className="ltw-label">Size (m², optional)</label>
              <input className="wiz-input" type="number" inputMode="numeric" min="0"
                     value={sizeSqm} placeholder="e.g. 85"
                     onChange={function(e) { setSizeSqm(e.target.value); }}/>
            </div>
            <div className="ltw-group">
              <label className="ltw-label">Furnishing</label>
              <div className="wiz-grid wiz-grid-types">
                <button type="button" className={"wiz-card " + (furnished ? "is-selected" : "")}
                        onClick={function() { setFurnished(true); }}>
                  <div className="wiz-card-icon">🛋</div>
                  <div className="wiz-card-label">Furnished</div>
                </button>
                <button type="button" className={"wiz-card " + (!furnished ? "is-selected" : "")}
                        onClick={function() { setFurnished(false); }}>
                  <div className="wiz-card-icon">📦</div>
                  <div className="wiz-card-label">Unfurnished</div>
                </button>
              </div>
            </div>
            <div className="ltw-group ltw-two">
              <div>
                <label className="ltw-label">Available from</label>
                <input type="date" className="wiz-input wiz-input-time" value={availableFrom} min={todayStr}
                       onChange={function(e) { setAvailableFrom(e.target.value); }}/>
              </div>
              <div>
                <label className="ltw-label">Minimum period (months)</label>
                <div className="fsheet-opts">
                  {[1,3,6,12].map(function(n) {
                    return <button key={n} type="button" className={"fsheet-opt" + (minMonths === n ? " on" : "")}
                                   onClick={function() { setMinMonths(n); }}>{n}</button>;
                  })}
                </div>
              </div>
            </div>
            <div className="ltw-group">
              <label className="ltw-label">Description</label>
              <textarea className="wiz-textarea" value={description} maxLength={1200}
                        placeholder="Describe the property, the compound, and the area…"
                        onChange={function(e) { setDescription(e.target.value); }}/>
            </div>
            <div className="ltw-group">
              <label className="ltw-label">House rules (optional)</label>
              <textarea className="wiz-textarea ltw-rules" value={houseRules} maxLength={600}
                        placeholder="e.g. No smoking indoors · Quiet hours after 11pm…"
                        onChange={function(e) { setHouseRules(e.target.value); }}/>
            </div>
            <div className="ltw-group">
              <label className="ltw-label">Amenities</label>
              <div className="fsheet-opts">
                {LT_AMENITIES.map(function(a) {
                  return (
                    <button key={a.id} type="button"
                            className={"wiz-card wiz-amen-card " + (amen.indexOf(a.id) !== -1 ? "is-selected" : "")}
                            onClick={function() { toggle(amen, setAmen, a.id); }}>
                      <div className="wiz-card-icon">{a.icon}</div>
                      <div className="wiz-card-label">{a.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="ltw-group">
              <label className="ltw-label">Shared spaces</label>
              <div className="fsheet-opts">
                <button type="button" className={"fsheet-opt" + (noShared ? " on" : "")}
                        onClick={function() { setNoShared(!noShared); if (!noShared) setShared([]); }}>No shared spaces</button>
                {!noShared && LT_SHARED.map(function(s) {
                  return <button key={s.id} type="button" className={"fsheet-opt" + (shared.indexOf(s.id) !== -1 ? " on" : "")}
                                 onClick={function() { toggle(shared, setShared, s.id); }}>{s.label}</button>;
                })}
              </div>
            </div>
          </>
        )}

        {/* ═══ STEP 3 — PHOTOS ═══ */}
        {step === 2 && (
          <>
            <div className="ltw-group">
              <label className="ltw-label">Photos ({photoFiles.length}/20 · minimum 5)</label>
              <label className={"wiz-photo-add ltw-drop" + (dragOver ? " over" : "")}
                     onDragOver={function(e) { e.preventDefault(); setDragOver(true); }}
                     onDragLeave={function() { setDragOver(false); }}
                     onDrop={onDrop}>
                <span className="wiz-photo-add-plus">+</span>
                <span className="ltw-drop-t">Tap to upload — or drag photos here</span>
                <span className="ltw-drop-s">The first photo is your cover. You can reorder below.</span>
                <input type="file" accept="image/*" multiple onChange={onPhotos}/>
              </label>
              <div className="ltw-photos">
                {photoPreviews.map(function(p, i) {
                  return (
                    <div key={i} className="ltw-photo">
                      <img src={p} alt=""/>
                      {i === 0 && <span className="wiz-photo-cover">Cover</span>}
                      <button type="button" className="ltw-photo-rm" onClick={function() { removePhoto(i); }}>×</button>
                      <div className="ltw-photo-mv">
                        <button type="button" disabled={i === 0} onClick={function() { movePhoto(i, -1); }}>◀</button>
                        <button type="button" disabled={i === photoPreviews.length - 1} onClick={function() { movePhoto(i, 1); }}>▶</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* ═══ STEP 4 — REVIEW ═══ */}
        {step === 3 && (
          <>
            <div className="ltw-review">
              <div className="ltw-rv-sec">
                <div className="ltw-rv-h">Property information</div>
                <div className="ltw-rv-row"><span>Title</span><strong>{title.trim()}</strong></div>
                <div className="ltw-rv-row"><span>Type</span><strong>{ltTypeLabel(propType)}</strong></div>
                <div className="ltw-rv-row"><span>Public area</span><strong>{area.trim()}</strong></div>
                <div className="ltw-rv-row"><span>Bedrooms · Bathrooms</span><strong>{bedrooms === 0 ? "Studio" : bedrooms} · {bathrooms}</strong></div>
                <div className="ltw-rv-row"><span>Max occupants</span><strong>{maxOccupants}</strong></div>
                {Number(sizeSqm) > 0 ? <div className="ltw-rv-row"><span>Size</span><strong>{sizeSqm} m²</strong></div> : null}
                <div className="ltw-rv-row"><span>Furnishing</span><strong>{furnished ? "Furnished" : "Unfurnished"}</strong></div>
              </div>
              <div className="ltw-rv-sec">
                <div className="ltw-rv-h">Monthly rent</div>
                <div className="ltw-rv-price">GMD {rentNum.toLocaleString()}<span> / month</span></div>
              </div>
              {amen.length > 0 && (
                <div className="ltw-rv-sec">
                  <div className="ltw-rv-h">Amenities</div>
                  <div className="ltw-rv-tags">{amen.map(function(id) {
                    var a = LT_AMENITIES.find(function(x) { return x.id === id; });
                    return a ? <span key={id}>{a.icon} {a.label}</span> : null;
                  })}</div>
                </div>
              )}
              <div className="ltw-rv-sec">
                <div className="ltw-rv-h">Photos ({photoPreviews.length})</div>
                <div className="ltw-rv-photos">{photoPreviews.slice(0, 8).map(function(p, i) {
                  return <img key={i} src={p} alt=""/>;
                })}</div>
              </div>
              <div className="ltw-rv-sec ltw-private">
                <div className="ltw-rv-h">🔒 Private address (never shown publicly)</div>
                <div className="ltw-rv-priv">{privAddress.trim()}</div>
                {privMaps.trim() ? <div className="ltw-rv-priv">{privMaps.trim()}</div> : null}
              </div>
            </div>
          </>
        )}

        <div className="ltw-nav">
          {error && <div className="ltw-nav-error">{error}</div>}
          <div className="ltw-nav-btns">
            {step > 0 && <button type="button" className="wiz-back" onClick={goBack} disabled={busy}>Back</button>}
            {step < 3 ? (
              <button type="button" className={"wiz-next " + (busy ? "off" : "")} onClick={goNext} disabled={busy}>Next</button>
            ) : (
              <button type="button" className={"wiz-next " + (busy ? "off" : "")} onClick={submit} disabled={busy}>Submit listing</button>
            )}
          </div>
        </div>
        <div className="bsheet-foot">Reviewed by KUNDA before going live · Private location stays private</div>
      </div>
    </div>
  );
}
